// src/components/AdminLayout.js
import React, { useState, useEffect } from 'react';
import {lerRetornoApresentacao,lerRetornoSalvo,destinoRetorno,restaurarTelaCheia,RETORNO_APRESENTACAO_KEY} from '../utils/retornoApresentacao';
import {abrirAnalyser} from '../utils/abrirAnalyser';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Gavel,
  FileText,
  Heart,
  FileSpreadsheet,
  FileBarChart2,
  ChevronLeft,
  ChevronRight,
  LogOut,
  ChevronDown,
  Search,
  Settings,
  Image,
  FileDown,
  Globe,
  MapPin,
  Phone,
  MessagesSquare,
  Smartphone
} from 'lucide-react';
import logoAzul from '../assets/logo-leilancer.png';
import { logout } from '../utils/session';
import Leila from './Leila';
import AvisosBuscas from './AvisosBuscas';
import './Workspace.css';
import './PlatformPolish.css';
import { idDaRef } from '../utils/refImovel';

function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  // No mobile a sidebar começa fechada (drawer overlay). No desktop, aberta (264px fixos).
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [user,setUser] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('user')); } catch { return null; }
  });
  const [refBusca, setRefBusca] = useState('');
  const [refAviso, setRefAviso] = useState('');

  // A caixa do topo abre o imóvel pela REF (LC-123456): é o código que vai no
  // PDF para o cliente, e quem recebe de volta "quero o LC-..." acha na hora.
  const abrirPelaRef = () => {
    const id = idDaRef(refBusca);
    if (!id) { setRefAviso('Digite a REF do imóvel, ex.: LC-123456'); return; }
    setRefAviso('');
    setRefBusca('');
    navigate(`/admin/analise/${id}`);
  };

  // Fecha sidebar ao trocar de rota no mobile (UX padrão de drawer)
  React.useEffect(() => {
    if (window.matchMedia('(max-width: 767px)').matches) {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  const handleLogout = () => logout('/login');

  // ========== VERIFICAÇÃO DE PERMISSÃO ==========
  const [fotoPerfil,setFotoPerfil]=useState(null);
  useEffect(()=>{
    let ativo=true;
    const atualizar=async()=>{try{
      const r=await fetch((process.env.REACT_APP_API_URL||'')+'/api/usuarios/me/perfil',{headers:{Authorization:'Bearer '+sessionStorage.getItem('token')}});
      if(!r.ok)return;const p=await r.json();if(!ativo)return;
      setFotoPerfil(p.foto||null);setUser(u=>({...u,name:p.name,email:p.email}));
    }catch{}};
    atualizar();window.addEventListener('leilancer-profile-updated',atualizar);
    return()=>{ativo=false;window.removeEventListener('leilancer-profile-updated',atualizar);};
  },[]);
  const userRole = user?.role || 'usuario';
  const [retornoSalvo,setRetornoSalvo]=useState(()=>lerRetornoSalvo(sessionStorage));
  const retorno=lerRetornoApresentacao(location.pathname,location.search)||(retornoSalvo?.path===location.pathname?retornoSalvo:null);
  const voltarApresentacao=userRole==='admin'&&retorno;
  useEffect(()=>{const novo=lerRetornoApresentacao(location.pathname,location.search);if(novo){sessionStorage.setItem(RETORNO_APRESENTACAO_KEY,JSON.stringify(novo));setRetornoSalvo(novo);}else{const anterior=lerRetornoSalvo(sessionStorage);if(anterior&&anterior.path!==location.pathname){sessionStorage.removeItem(RETORNO_APRESENTACAO_KEY);setRetornoSalvo(null);}}},[location.pathname,location.search]);

  // ========== MENU ITEMS COM PERMISSÕES ==========
  const menuItems = [
    { icon: FileText, label: 'Apresentações', path: '/admin/apresentacoes', allowedRoles: ['admin'] },
    { icon: MessagesSquare, label: 'Depoimentos', path: '/admin/depoimentos', allowedRoles: ['admin'] },
    { icon: Users, label: 'CRM', path: '/admin/crm', allowedRoles: ['admin'] },
    {
      icon: LayoutDashboard,
      label: 'Dashboard',
      path: '/admin/dashboard',
      allowedRoles: ['admin', 'moderador', 'usuario', 'marketing']
    },
    {
      icon: Search,
      label: 'Buscar Imóveis',
      path: '/admin/buscar',
      allowedRoles: ['admin', 'moderador', 'usuario']
    },
    {
      icon: Gavel,
      label: 'Gestão de Leilões',
      path: '/admin/leiloes',
      allowedRoles: ['admin']
    },
    {
      icon: Gavel,
      label: 'Leiloeiros',
      path: '/admin/leiloeiros',
      allowedRoles: ['admin']
    },
    {
      // A planilha de pré-análise. Fica no primeiro nível, e não escondida num
      // submenu, porque é a tela de trabalho do dia a dia.
      icon: FileSpreadsheet,
      label: 'Gerenciador de Imóvel',
      path: '/admin/gerenciador',
      allowedRoles: ['admin', 'moderador']
    },
    {
      icon: Gavel,
      label: 'Leilões',
      allowedRoles: ['admin', 'moderador', 'usuario'],
      // Itens do submenu — todos sob /admin/* pra manter o usuário dentro do
      // layout administrativo (clicar em algo de fora do /admin sumia com a
      // sidebar e confundia o admin).
      submenu: [
        { label: 'Buscar Leilões', path: '/admin/buscar-leiloes' },
        { label: 'Auditoria de Scrapers', path: '/admin/auditoria' },
      ]
    },
    { icon: Search, label: 'Minhas buscas', path: '/admin/minhas-buscas', allowedRoles: ['admin', 'moderador', 'usuario'] },
    {
      // As análises salvas da calculadora. Existiam só no menu do usuário
      // (/dashboard), então quem trabalha pelo painel administrativo não tinha
      // como chegar nas próprias análises.
      icon: FileBarChart2,
      label: 'Minhas Análises',
      path: '/admin/analises',
      allowedRoles: ['admin', 'moderador', 'usuario']
    },
    { icon: Search, label: 'Leilancer Analyser', path: '/admin/analyser', analyser:true, allowedRoles:['admin','moderador','usuario'] },
    {
      icon: FileText,
      label: 'Relatórios',
      path: '/admin/relatorios',
      allowedRoles: ['admin', 'moderador', 'usuario', 'marketing']
    },
    {
      icon: Image,
      label: 'Banners',
      path: '/admin/banners',
      allowedRoles: ['admin', 'marketing']
    },
    { icon: FileDown, label: 'Vitrine de cursos', path: '/admin/educacao/vitrine', allowedRoles: ['admin','educacao'] },
    { icon: FileDown, label: 'Área de Membros', path: '/admin/membros', allowedRoles: ['admin','educacao'] },
    { icon: Users, label: 'Acessos da educação', path: '/admin/educacao/acessos', allowedRoles: ['admin'] },
    { icon: FileDown, label: 'Vendas', path: '/admin/educacao/vendas', allowedRoles: ['admin','educacao'] },
    { icon: FileDown, label: 'Meus produtos', path: '/admin/educacao/produtos', allowedRoles: ['admin','educacao'] },
    { icon: FileDown, label: 'Novo produto', path: '/admin/educacao/produtos/novo', allowedRoles: ['admin','educacao'] },
    { icon: FileDown, label: 'Contratos', path: '/admin/educacao/contratos', allowedRoles: ['admin','educacao'] },
    { icon: FileDown, label: 'Leads de educação', path: '/admin/educacao/leads-educacao', allowedRoles: ['admin','educacao'] },
    { icon: FileDown, label: 'Relatórios de educação', path: '/admin/educacao/relatorios-educacao', allowedRoles: ['admin','educacao'] },
    {
      icon: FileDown,
      label: 'Materiais',
      path: '/admin/materiais',
      allowedRoles: ['admin', 'marketing']
    },
    {
      icon: FileSpreadsheet,
      label: 'Importar Planilha',
      path: '/admin/importar',
      allowedRoles: ['admin']
    },
    {
      icon: Settings,
      label: 'Configurações',
      path: '/admin/configuracoes',
      allowedRoles: ['admin', 'moderador', 'usuario']
    },
    {
      icon: Heart,
      label: 'Favoritos',
      path: '/admin/favoritos',
      allowedRoles: ['admin', 'moderador', 'usuario']
    },
    {
      icon: Globe,
      label: 'Landing /comece-aqui',
      path: '/admin/landing',
      allowedRoles: ['admin', 'gerente', 'marketing']
    },
    {
      icon: MapPin,
      label: 'Mapa de Leilões',
      path: '/admin/mapa',
      allowedRoles: ['admin', 'moderador', 'usuario']
    },
    {
      // O que as pessoas pedem à assistente — e, principalmente, o que ela não
      // soube ler e o que o acervo não tinha.
      icon: MessagesSquare,
      label: 'Conversas da Leila',
      path: '/admin/leila',
      allowedRoles: ['admin', 'gerente', 'moderador']
    },
    {
      icon: Phone,
      label: 'Leads Capturados',
      path: '/admin/leads',
      allowedRoles: ['admin', 'gerente', 'marketing']
    },
    {
      icon: Users,
      label: 'Usuários',
      path: '/admin/usuarios',
      allowedRoles: ['admin']
    },
    {
      // O número que envia o código do cadastro e os avisos de imóvel novo.
      // Só admin: desconectar daqui para os dois na hora.
      icon: Smartphone,
      label: 'WhatsApp',
      path: '/admin/whatsapp',
      allowedRoles: ['admin']
    },
  ];

  // ========== FILTRAR MENU POR PERMISSÃO ==========
  const filteredMenuItems = menuItems.filter(item => 
    item.allowedRoles.includes(userRole)
  );

  return (
    <div className="lc-shell flex flex-col h-screen overflow-hidden relative">
        <header className="lc-topbar h-16 border-b flex items-center justify-between gap-3 px-4 md:px-6 flex-shrink-0 relative z-50">
          <Link to="/" aria-label="Leilancer — início" className="flex-shrink-0">
            <img src={logoAzul} alt="Leilancer" className="h-8 w-auto max-w-[100px] sm:max-w-[160px] object-contain" />
          </Link>

          

          <div className="flex items-center justify-end gap-2 flex-1 min-w-0">
            {!['educacao','marketing'].includes(userRole)&&<div className="relative w-40 sm:w-56 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                type="text"
                value={refBusca}
                onChange={(e) => { setRefBusca(e.target.value); if (refAviso) setRefAviso(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') abrirPelaRef(); }}
                aria-label="Buscar imóvel pela referência"
                placeholder="Buscar por REF"
                className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-2 py-1.5 text-xs focus:outline-none focus:border-primary transition"
              />
              {refAviso && (
                <div className="absolute left-0 top-full mt-1 text-[11px] text-amber-300">{refAviso}</div>
              )}
            </div>}

          </div>
          {!['educacao','marketing'].includes(userRole)&&<AvisosBuscas />}
          {!['educacao','marketing'].includes(userRole)&&<button onClick={() => navigate('/admin/configuracoes')} aria-label="Configurações" className="p-2 hover:bg-white/5 rounded-lg transition">
            <Settings className="w-5 h-5" />
          </button>}
        </header>
      <div className="relative flex flex-1 min-h-0 overflow-hidden">
          <button
            type="button"
            onClick={() => setSidebarOpen(v => !v)}
            className={`lc-sidebar-toggle absolute top-2 ${sidebarOpen ? "left-[212px]" : "left-1"} z-50 p-2 min-h-[44px] min-w-[44px] md:min-h-[32px] md:min-w-[32px] flex-shrink-0 rounded-lg opacity-80 hover:opacity-100 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary`}
            aria-label={sidebarOpen ? "Recolher menu lateral" : "Abrir menu lateral"}
            title={sidebarOpen ? "Recolher menu lateral" : "Abrir menu lateral"}
            aria-expanded={sidebarOpen}
            aria-controls="menu-lateral"
          >
            {sidebarOpen ? <ChevronLeft className="w-5 h-5" strokeWidth={3} /> : <ChevronRight className="w-5 h-5" strokeWidth={3} />}
          </button>
      {/* Backdrop mobile: clicar fora fecha o drawer */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 z-30"
          onClick={() => setSidebarOpen(false)}
          aria-hidden
        />
      )}
      {/* Sidebar — no mobile vira drawer overlay (fixed + transform).
          No desktop a coluna pode ser totalmente recolhida. */}
      <aside
        id="menu-lateral"
        aria-label="Menu principal"
        hidden={!sidebarOpen}
        style={!sidebarOpen ? { display: 'none' } : undefined}
        className={`
          w-64 flex-shrink-0
          lc-sidebar bg-dark text-white border-r border-white/10 transition-all duration-300 flex flex-col
          fixed md:relative top-16 bottom-0 md:top-auto md:bottom-auto left-0 z-40
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:hidden'}
        `}
      >
        {/* Menu */}
        <nav className="flex-1 overflow-y-auto pt-14 pb-4 px-2 space-y-1">
          {[
            ['Pesquisa', ['buscar', 'mapa', 'favoritos']],
            ['Análises', ['dashboard', 'gerenciador', 'minhas-buscas', 'analises', 'analyser', 'relatorios']],
            ['Operação', ['leiloeiros', 'leiloes', 'importar']],
            ['Produtos', ['produtos', 'novo', 'membros', 'vitrine']],
            ['Vendas', ['acessos','vendas', 'contratos', 'leads-educacao', 'relatorios-educacao']],
            ['Conteúdo', ['apresentacoes', 'depoimentos', 'banners', 'materiais', 'landing']],
            ['Clientes', ['crm', 'leila', 'leads', 'usuarios', 'whatsapp']],
            ['Conta', ['configuracoes']],
          ].map(([grupo, caminhos]) => {
            const itens = filteredMenuItems.filter(item => caminhos.includes(item.path?.split('/').pop() || 'leiloes'));
            return itens.length ? <div key={grupo} className="lc-nav-group">
              {sidebarOpen && <p className="lc-nav-heading">{grupo}</p>}
              {itens.map(item => <MenuItem key={item.label} item={item} sidebarOpen={sidebarOpen} currentPath={location.pathname} onAnalyser={()=>abrirAnalyser(location,navigate)} />)}
            </div> : null;
          })}
        </nav>

        {/* User info */}
        <div className="border-t border-white/10 p-4">
          <div className={`flex items-center gap-3 ${!sidebarOpen && 'justify-center'}`}>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-[#E2BF77] flex items-center justify-center font-bold">
              {fotoPerfil ? <img src={fotoPerfil} alt="Sua foto de perfil" className="w-full h-full rounded-full object-cover" /> : user?.name?.charAt(0)}
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{user?.name?.split(' ')[0]}</p>
                <p className="text-xs text-white/60 truncate">{user?.email}</p>
                {/* Badge de permissão */}
                <span className={`inline-block mt-1 px-2 py-0.5 text-[10px] font-bold rounded ${
                  userRole === 'admin' ? 'bg-red-500/20 text-red-400' :
                  userRole === 'moderador' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-gray-500/20 text-gray-400'
                }`}>
                  {userRole.toUpperCase()}
                </span>
              </div>
            )}
          </div>
          <button
            onClick={handleLogout}
            className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary hover:bg-primary-hover rounded-lg text-sm font-semibold transition"
          >
            <LogOut className="w-4 h-4" />
            {sidebarOpen && 'Sair'}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top Bar */}


        {/* Content Area */}
        <div className="lc-workspace flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6">
          {voltarApresentacao && <Link to={destinoRetorno(retorno)} onClick={e=>{if(e.button===0&&!e.ctrlKey&&!e.metaKey&&!e.shiftKey&&!e.altKey)restaurarTelaCheia(retorno);sessionStorage.removeItem(RETORNO_APRESENTACAO_KEY);setRetornoSalvo(null);}} className="inline-flex self-start items-center text-xs opacity-80 hover:opacity-100 mb-3" style={{color:'var(--lc-text, #b5bdb8)',textDecoration:'none'}}>← Voltar à apresentação</Link>}
          <Outlet />
        </div>
      </main>
      </div>

      {/* Assistente de busca — acompanha o usuário em todas as telas logadas */}
      {!['educacao','marketing'].includes(userRole)&&<Leila />}
    </div>
  );
}

function MenuItem({ item, sidebarOpen, currentPath, onAnalyser }) {
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const hasSubmenu = item.submenu && item.submenu.length > 0;
  const Icon = item.icon;
  const isActive = currentPath === item.path || (hasSubmenu && item.submenu.some(sub => currentPath === sub.path));

  if(item.analyser)return <button type="button" title="Investigação aprofundada" aria-label="Leilancer Analyser" onClick={onAnalyser} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg transition group hover:bg-white/5"><Icon className="w-5 h-5 flex-shrink-0"/>{sidebarOpen&&<span className="text-sm font-medium">Leilancer Analyser</span>}</button>;
  if (hasSubmenu) {
    return (
      <div>
        <button
          aria-label={item.label}
          aria-expanded={submenuOpen}
          title={!sidebarOpen ? item.label : undefined}
          onClick={() => setSubmenuOpen(!submenuOpen)}
          className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition group ${
            isActive ? 'bg-primary text-white' : 'hover:bg-white/5'
          }`}
        >
          <Icon className="w-5 h-5 flex-shrink-0" />
          {sidebarOpen && (
            <>
              <span className="flex-1 text-left text-sm font-medium">{item.label}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${submenuOpen ? 'rotate-180' : ''}`} />
            </>
          )}
        </button>
        {sidebarOpen && submenuOpen && (
          <div className="ml-8 mt-1 space-y-1">
            {item.submenu.map((sub, idx) => (
              <Link
                key={idx}
                to={sub.path}
                className={`block px-3 py-2 rounded-lg text-sm transition ${
                  currentPath === sub.path ? 'bg-white/10 text-white' : 'text-white/70 hover:bg-white/5 hover:text-white'
                }`}
              >
                {sub.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={item.path}
      aria-label={item.label}
      aria-current={isActive ? 'page' : undefined}
      title={!sidebarOpen ? item.label : undefined}
      className={`flex items-center gap-3 px-3 py-3 rounded-lg transition group ${
        isActive ? 'bg-primary text-white' : 'hover:bg-white/5'
      }`}
    >
      <Icon className="w-5 h-5 flex-shrink-0" />
      {sidebarOpen && <span className="text-sm font-medium">{item.label}</span>}
    </Link>
  );
}

export default AdminLayout;
